import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import DOMPurify from "dompurify";
import { RendererProps } from "../FilePreviewModal";

const MAX_PREVIEW_BYTES = 10 * 1024 * 1024;

const HtmlRenderer: React.FC<RendererProps> = ({ blob, fileName }) => {
  const [sanitizedUrl, setSanitizedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    if (!blob) return;
    const fileBlob = blob;
    let cancelled = false;
    let createdUrl: string | null = null;

    const run = async () => {
      try {
        const slice =
          fileBlob.size > MAX_PREVIEW_BYTES
            ? fileBlob.slice(0, MAX_PREVIEW_BYTES)
            : fileBlob;
        const text = await slice.text();
        if (cancelled) return;

        const clean = DOMPurify.sanitize(text, {
          WHOLE_DOCUMENT: true,
          FORBID_TAGS: ["script", "object", "embed", "iframe", "form"],
          FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
        });

        const sanitizedBlob = new Blob([clean], { type: "text/html" });
        createdUrl = URL.createObjectURL(sanitizedBlob);
        setSanitizedUrl(createdUrl);
        setTruncated(fileBlob.size > MAX_PREVIEW_BYTES);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to read HTML");
        }
      }
    };
    void run();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [blob]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (!sanitizedUrl) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {truncated && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          File is larger than 10 MB — showing only the first part.
        </Alert>
      )}
      <iframe
        src={sanitizedUrl}
        title={fileName}
        sandbox="allow-same-origin"
        style={{
          flex: 1,
          width: "100%",
          height: "100%",
          border: 0,
          backgroundColor: "white",
        }}
      />
    </Box>
  );
};

export default HtmlRenderer;
